/**
 * Územie mesta Prešov na mape aj vo formulári.
 *
 * Polygón sem **nepatrí ako kópia** - appka si ho ťahá z API
 * (`GET /geo/boundary`, [getCityBoundary.ts](../features/queries/getCityBoundary.ts)),
 * lebo podľa toho istého poľa rozhoduje `createReport`. Keby tu ležala vlastná
 * kópia, časom by sa rozišli a používateľ by dostal 400 na bod, ktorý mu mapa
 * dovolila vybrať.
 *
 * `isInsideCity` je zámerne **to isté ray casting**, aké má API v Node, a nie
 * ArcGIS `containsOperator`: je synchrónne, nepotrebuje načítanú mapu (volá ho
 * aj validácia formulára a filtrovanie ponuky hľadania) a so serverom sa
 * zhoduje z podstaty, nie zhodou okolností. ArcGIS tu robí len to, čo ray
 * casting nevie - masku a prepočet mierky.
 */

/** `[lon, lat]` vo WGS84, ako to chodí z API. */
export type Position = [number, number];

/** Uzavretý vonkajší prstenec hranice mesta. */
export type CityRing = Position[];

/** `[minLon, minLat, maxLon, maxLat]` */
export type Bbox = [number, number, number, number];

export const ringBbox = (ring: CityRing): Bbox => {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  for (const [lon, lat] of ring) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  return [minLon, minLat, maxLon, maxLat];
};

/**
 * Leží bod na území mesta? Crossing number nad jedným uzavretým prstencom -
 * polygón nemá diery ani viac častí.
 *
 * Bez hranice (API nedostupné) vracia `true`: mapa je len predbežná kontrola,
 * tvrdú poistku drží server. Appka sa nemá zablokovať preto, že nedobehol
 * jeden dekoračný dotaz.
 */
export const isInsideCity = (
  ring: CityRing | undefined,
  lon: number,
  lat: number,
): boolean => {
  if (!ring || ring.length < 4) return true;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;

  const [minLon, minLat, maxLon, maxLat] = ringBbox(ring);
  if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];

    if (
      yi > lat !== yj > lat &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Hranice web mercatora. Maska je obdĺžnik celého sveta s dierou v tvare mesta,
 * takže musí siahať tam, kam siaha podklad - ďalej sa aj tak nedá posunúť.
 */
const WM_X = 20037508.342789244;
const WM_Y = 20048966.104014594;

/**
 * Farby masky. Neutrálne šedé - podklad je `gray-vector`.
 *
 * Závoj aj šrafúra sú zámerne dosť priesvitné, aby bolo okolie mesta ešte
 * čitateľné: názvy susedných obcí a cesty von z mesta sú orientačný bod,
 * nie šum. Maska má povedať „sem podnet nepatrí", nie okolie zakryť.
 * Obrys hranice zostáva plný - to je tá čiara, ktorá to celé drží.
 */
const VEIL_COLOR = [255, 255, 255, 0.3];
const HATCH_COLOR = [120, 130, 140, 0.17];
const BORDER_COLOR = [88, 98, 110, 0.9];

/**
 * Vrstva, ktorá zašedí a vyšrafuje všetko mimo mesta a obtiahne hranicu.
 *
 * Diera sa **nerobí ručným poradím prstencov** (vonkajší po smere hodinových
 * ručičiek, diera proti smeru). To je klasická tichá chyba: pri zlom poradí sa
 * buď nevykreslí nič, alebo sa vyplní práve mesto a zvyšok zostane čistý.
 * Namiesto toho sa oba polygóny prevedú cez `simplifyOperator` (ten orientáciu
 * znormalizuje sám) a odčítajú cez `differenceOperator`.
 *
 * Geometria sa počíta rovno vo web mercatore, nie vo WGS84: podklad je v ňom,
 * takže odpadá projekcia pri každom vykreslení, a obdĺžnik sveta sa v ňom dá
 * vyjadriť presne (vo WGS84 by ±90° bolo v mercatore nekonečno).
 */
export const createCityMaskLayer = async (ring: CityRing) => {
  const [
    { default: GraphicsLayer },
    { default: Graphic },
    { default: Polygon },
    { default: SpatialReference },
    webMercatorUtils,
    { execute: difference },
    { execute: simplify },
  ] = await Promise.all([
    import('@arcgis/core/layers/GraphicsLayer'),
    import('@arcgis/core/Graphic'),
    import('@arcgis/core/geometry/Polygon'),
    import('@arcgis/core/geometry/SpatialReference'),
    import('@arcgis/core/geometry/support/webMercatorUtils'),
    import('@arcgis/core/geometry/operators/differenceOperator'),
    import('@arcgis/core/geometry/operators/simplifyOperator'),
  ]);

  const boundary = simplify(
    webMercatorUtils.geographicToWebMercator(
      new Polygon({ rings: [ring], spatialReference: SpatialReference.WGS84 }),
    ) as __esri.Polygon,
  ) as __esri.Polygon;

  const world = simplify(
    new Polygon({
      rings: [
        [
          [-WM_X, -WM_Y],
          [WM_X, -WM_Y],
          [WM_X, WM_Y],
          [-WM_X, WM_Y],
          [-WM_X, -WM_Y],
        ],
      ],
      spatialReference: SpatialReference.WebMercator,
    }),
  ) as __esri.Polygon;

  const mask = difference(world, boundary) as __esri.Polygon | null;

  const layer = new GraphicsLayer({
    id: 'city-boundary-mask',
    title: 'Územie mesta',
    listMode: 'hide',
    // Do klikania nevstupuje. `FeedsMap` si aj tak vymenúva vrstvy
    // v `hitTest({ include })` a mapa vo formulári žiadny `hitTest` nerobí -
    // číta `mapPoint` priamo z udalosti. `GraphicsLayer` popupy sám od seba
    // nemá, tak sa nevypínajú.
  });

  if (mask) {
    layer.addMany([
      // Závoj a šrafúra sú dve grafiky zámerne: jeden `SimpleFillSymbol` má
      // jeden `style`, takže plnú výplň a šrafovanie naraz spraviť nevie.
      new Graphic({
        geometry: mask,
        symbol: {
          type: 'simple-fill',
          style: 'solid',
          color: VEIL_COLOR,
          outline: { width: 0 },
        } as unknown as __esri.SimpleFillSymbol,
      }),
      new Graphic({
        geometry: mask,
        symbol: {
          type: 'simple-fill',
          style: 'forward-diagonal',
          color: HATCH_COLOR,
          outline: { width: 0 },
        } as unknown as __esri.SimpleFillSymbol,
      }),
    ]);
  }

  layer.add(
    new Graphic({
      geometry: boundary,
      symbol: {
        type: 'simple-fill',
        style: 'none',
        outline: { color: BORDER_COLOR, width: 2 },
      } as unknown as __esri.SimpleFillSymbol,
    }),
  );

  return { layer, extent: boundary.extent };
};

/**
 * Koľko voľnosti navyše dostane najmenšie priblíženie oproti presnému
 * napasovaniu mesta do plochy.
 *
 * Presné napasovanie (1,0) vyzerá na papieri správne, ale v appke nie: nad
 * mapou pláva panel so zoznamom (412 px na desktope) a na mobile spodný list,
 * takže východná časť mesta skončí **pod nimi**. `ArcgisMap` o paneli nevie
 * a `view.padding` sa použiť nedá - `FeedsMap` si odsadenie detailu k panelu už
 * počíta sám (`DETAIL_OFFSET_Y`) a dvakrát by sa to odčítalo.
 *
 * **2,0 je presne jedna celá úroveň priblíženia** pod napasovanie (úrovne
 * podkladu idú po mocninách dvojky), takže mesto vyjde na polovicu plochy
 * a okolo neho je vidieť okolie.
 *
 * Byť tu štedrý nič nestojí: odísť mimo mesta to nedovolí, to drží
 * `constraints.geometry`, nie mierka.
 */
const MIN_SCALE_SLACK = 2.0;

/**
 * Najmenšia mierka, pri ktorej je mesto ešte celé v zábere.
 *
 * Počíta sa z rozmerov plochy, nie z pevnej úrovne priblíženia: pevné
 * `minZoom` by na mobile ukázalo menej než celé mesto a na širokom monitore
 * zbytočne veľa. Preto sa prepočítava aj pri zmene veľkosti okna.
 *
 * `96 * 39.37` je prevod rozlíšenia (metre na pixel) na mierku tak, ako ju
 * počíta ArcGIS - 96 dpi a palcov na meter.
 */
export const minScaleFor = (
  extent: __esri.Extent,
  widthPx: number,
  heightPx: number,
): number | undefined => {
  if (!widthPx || !heightPx) return undefined;

  const resolution =
    Math.max(extent.width / widthPx, extent.height / heightPx) *
    MIN_SCALE_SLACK;

  return resolution * 96 * 39.37;
};
